import {
  MetronomeSound,
  MetronomeTockSound,
} from "./metronome_config";

export const METRONOME_SOUND_ASSETS: Record<MetronomeSound, string> = {
  metronome: "/assets/metronome/metronome.flac",
  drumstick: "/assets/metronome/drumstick.flac",
  tincan: "/assets/metronome/tincan.flac",
  woodblock: "/assets/metronome/woodblock.flac",
};

type IntervalHandle = ReturnType<typeof setInterval>;
type LoadBuffer = (url: string, context: AudioContext) => Promise<AudioBuffer>;

export interface MetronomePreviewDependencies {
  createAudioContext?: () => AudioContext;
  loadBuffer?: LoadBuffer;
  setInterval?: typeof setInterval;
  clearInterval?: typeof clearInterval;
}

async function loadAudioBuffer(url: string, context: AudioContext): Promise<AudioBuffer> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Unable to load metronome sound: ${url}`);
  return context.decodeAudioData(await response.arrayBuffer());
}

export class MetronomePreview {
  private context: AudioContext | null = null;
  private buffers = new Map<MetronomeSound, AudioBuffer>();
  private interval: IntervalHandle | null = null;
  private generation = 0;
  private patternGeneration = 0;
  private _playing = false;
  private beatsSinceTock = 0;
  private tickBuffer: AudioBuffer | null = null;
  private tockBuffer: AudioBuffer | null = null;
  private tockEvery = 4;
  private readonly createAudioContext: () => AudioContext;
  private readonly loadBuffer: LoadBuffer;
  private readonly scheduleInterval: typeof setInterval;
  private readonly cancelInterval: typeof clearInterval;

  constructor(dependencies: MetronomePreviewDependencies = {}) {
    this.createAudioContext =
      dependencies.createAudioContext ||
      (() => {
        const AudioContextConstructor =
          window.AudioContext || (window as any).webkitAudioContext;
        return new AudioContextConstructor();
      });
    this.loadBuffer = dependencies.loadBuffer || loadAudioBuffer;
    this.scheduleInterval =
      dependencies.setInterval ||
      ((handler, timeout, ...args) => globalThis.setInterval(handler, timeout, ...args));
    this.cancelInterval =
      dependencies.clearInterval || ((interval) => globalThis.clearInterval(interval));
  }

  get playing(): boolean {
    return this._playing;
  }

  async start(
    speed: number,
    soundTick: MetronomeSound,
    soundTock: MetronomeTockSound,
    tockEvery: number,
    playImmediately = true
  ): Promise<void> {
    this.stop();
    const generation = this.generation;
    const patternGeneration = ++this.patternGeneration;
    const tickBuffer = await this.getBuffer(soundTick);
    const tockBuffer =
      soundTock === "none" ? null : await this.getBuffer(soundTock);
    if (
      generation !== this.generation ||
      patternGeneration !== this.patternGeneration
    ) {
      return;
    }

    this._playing = true;
    this.beatsSinceTock = 0;
    this.tickBuffer = tickBuffer;
    this.tockBuffer = tockBuffer;
    this.tockEvery = tockEvery;
    if (playImmediately) this.playNextBeat();
    this.interval = this.scheduleInterval(
      () => this.playNextBeat(),
      60000 / speed
    );
  }

  async updatePattern(
    soundTick: MetronomeSound,
    soundTock: MetronomeTockSound,
    tockEvery: number
  ): Promise<void> {
    const generation = this.generation;
    const patternGeneration = ++this.patternGeneration;
    const tickBuffer = await this.getBuffer(soundTick);
    const tockBuffer =
      soundTock === "none" ? null : await this.getBuffer(soundTock);
    if (
      generation !== this.generation ||
      patternGeneration !== this.patternGeneration
    ) {
      return;
    }

    this.tickBuffer = tickBuffer;
    this.tockBuffer = tockBuffer;
    this.tockEvery = tockEvery;
  }

  async playOnce(sound: MetronomeSound): Promise<void> {
    const generation = this.generation;
    const buffer = await this.getBuffer(sound);
    if (generation === this.generation) this.playBuffer(buffer);
  }

  stop(): void {
    this.generation += 1;
    this.patternGeneration += 1;
    this._playing = false;
    if (this.interval !== null) {
      this.cancelInterval(this.interval);
      this.interval = null;
    }
  }

  async dispose(): Promise<void> {
    this.stop();
    if (this.context && this.context.state !== "closed") {
      await this.context.close();
    }
    this.context = null;
    this.buffers.clear();
  }

  private getContext(): AudioContext {
    if (!this.context) this.context = this.createAudioContext();
    return this.context;
  }

  private async getBuffer(sound: MetronomeSound): Promise<AudioBuffer> {
    const context = this.getContext();
    if (context.state === "suspended") await context.resume();

    let buffer = this.buffers.get(sound);
    if (!buffer) {
      buffer = await this.loadBuffer(METRONOME_SOUND_ASSETS[sound], context);
      this.buffers.set(sound, buffer);
    }
    return buffer;
  }

  private playNextBeat(): void {
    if (
      !this.context ||
      !this._playing ||
      !this.tickBuffer
    ) {
      return;
    }
    this.beatsSinceTock += 1;
    if (this.beatsSinceTock >= this.tockEvery) {
      this.playBuffer(this.tockBuffer || this.tickBuffer);
      this.beatsSinceTock = 0;
      return;
    }
    this.playBuffer(this.tickBuffer);
  }

  private playBuffer(buffer: AudioBuffer): void {
    if (!this.context) return;
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.context.destination);
    source.start();
  }
}
